/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { GREEK_IDX, OPTION_IDX, RawGreeksDataMap } from 'types';
import {
  PositionLeg,
  PositionShares,
  IVAdjustmentSettings,
  NEW_POSITION_ID,
  StrategyLeg,
  StrategyMirrorLeg,
  StrategyType,
  OptionType,
  Position,
  OC_DEFAULT_CALL_SLOPE_MULT,
  OC_DEFAULT_LEVEL_SHIFT,
  OC_DEFAULT_PUT_SLOPE_MULT,
  PositionDeletes,
} from 'types/optionsCalculator';
import { ModelParameters, TableData } from 'types/riskEngine';
import {
  CLOSURES,
  dayjs,
  ET,
  findNearestIdx,
  MILLISECONDS_PER_DAY,
  MILLISECONDS_PER_HOUR,
  YEAR_IN_MS,
} from './shared';
import { STRATEGY_CONFIGS } from 'config/optionsCalculator';
import { CONTRACT_MULTIPLIER } from 'config/riskEngine';
import { findD50Price, getMidpointPrice } from 'util/iVol';
import { calculateImpliedVolatilities } from 'sg-risk-engine';
import {
  resolveStyleForExpiry,
  type ExerciseStylesRecord,
} from 'util/moviefoneExerciseStyles';

import {
  calculateOptionPriceAndGreeks,
  getSharesDelta,
  getThetaPerDay,
  getTteYearsFromBaseDate,
  GreeksValues,
} from './riskEngine';

export const shortIdGenerator = (): number => {
  return Math.floor(Math.random() * 1_000_000);
};

// Format: SYM_YYMMDD_TYPE_STRIKE_QTY_PRICE_ID
// Example: SPY_1731840000000_C_560_1_12.35_abc123
export const stringifyLeg = (leg: PositionLeg): string =>
  `${leg.sym}_${leg.expiry}_${leg.option_type}_${leg.strike}_${leg.quantity}_${leg.price_paid}_${leg.leg_id}`;

export const stringifyShare = (share: PositionShares): string => {
  return `${share.sym}_${share.quantity}_${share.price_paid}`;
};

export const parseLegString = (legStr: string): PositionLeg | null => {
  const parts = legStr.split('_');
  if (parts.length !== 7) {
    return null;
  }

  const [sym, expiryStr, typeChar, strikeStr, qtyStr, priceStr, id] = parts;

  return {
    position_id: NEW_POSITION_ID,
    leg_id: parseInt(id, 10),
    sym,
    quantity: parseInt(qtyStr, 10),
    strike: parseFloat(strikeStr),
    expiry: Number(expiryStr),
    option_type: typeChar as OptionType,
    price_paid: parseFloat(priceStr),
  };
};

export const parseShareString = (shareStr: string): PositionShares | null => {
  const parts = shareStr.split('_');
  if (parts.length !== 3) {
    return null;
  }

  const [sym, size, price] = parts;

  return {
    sym,
    quantity: parseInt(size, 10),
    price_paid: parseFloat(price),
  };
};

export const stringifyIVSettings = (
  settings: Partial<IVAdjustmentSettings>,
): string => {
  const { level_shift, call_slope_mult, put_slope_mult, expiry } = settings;
  return `${expiry}:${level_shift}:${call_slope_mult}:${put_slope_mult}`;
};

export const parseIVSettings = (
  str: string,
): Partial<IVAdjustmentSettings> | null => {
  const parts = str.split(':');
  if (parts.length !== 4) {
    return null;
  }

  const [expiry, levelShift, callSlopeMult, putSlopeMult] = parts;
  return {
    expiry: parseInt(expiry, 10),
    level_shift: parseFloat(levelShift),
    call_slope_mult: parseFloat(callSlopeMult),
    put_slope_mult: parseFloat(putSlopeMult),
  };
};

/**
 * Builds the `useOptionsCalculator` query-string (sym/bd/legs/shares/ivs) for
 * a position, so a canvas widget's config-driven position can be reproduced
 * on the URL-synced standalone page — see the Share button in
 * `OptionsCalculatorContainer`.
 */
export const getStringifiedPosition = (
  position: Position,
  baseDate?: number,
): string => {
  const parts: string[] = [`sym=${encodeURIComponent(position.sym)}`];

  if (baseDate != null) {
    parts.push(`bd=${baseDate}`);
  }
  if (position.option_legs.length > 0) {
    parts.push(`legs=${position.option_legs.map(stringifyLeg).join(',')}`);
  }
  if (position.underlying_shares.length > 0) {
    parts.push(
      `shares=${position.underlying_shares.map(stringifyShare).join(',')}`,
    );
  }
  if (position.iv_adjustments.length > 0) {
    parts.push(
      `ivs=${position.iv_adjustments.map(stringifyIVSettings).join(',')}`,
    );
  }

  return parts.join('&');
};

export function getAdjustedIV(
  initialIV: number,
  minIV: number,
  adjustmentParams: IVAdjustmentSettings,
  isCall: boolean,
): number {
  const adjustedIV =
    minIV +
    adjustmentParams.level_shift +
    (initialIV - minIV) *
      (isCall
        ? adjustmentParams.call_slope_mult
        : adjustmentParams.put_slope_mult);

  // Ensure IV/IV-adjusted stays positive
  return Math.max(0, adjustedIV);
}

export function getAdjustedVolData(
  initialVolData: TableData[],
  ivSettings: IVAdjustmentSettings[],
): TableData[] {
  return initialVolData.map((data: TableData) => {
    const { tte, strikePrices } = data;
    const ivSetting = ivSettings?.find((s) => s.expiry === tte);
    if (ivSetting == null) {
      return data;
    }

    // get the strike with the minimum IV
    const [minIVStrike, minIV] = Object.entries(strikePrices).reduce(
      ([minStrike, minVol], [strike, iv]) =>
        iv < minVol ? [strike, iv] : [minStrike, minVol],
      [Object.keys(strikePrices)[0], Infinity],
    );

    // Process strike prices in one pass
    const updatedStrikeVols = Object.entries(strikePrices).reduce(
      (acc, [strike, iv]) => {
        acc[Number(strike)] = getAdjustedIV(
          iv,
          minIV,
          ivSetting,
          Number(strike) >= Number(minIVStrike),
        );
        return acc;
      },
      {} as Record<number, number>,
    );

    return { ...data, strikePrices: updatedStrikeVols };
  });
}

// Rounds a leg price for display only - the stored price_paid keeps full precision.
export const roundToCents = (price: number | null): number | null =>
  price == null ? null : Math.round(price * 100) / 100;

export const formatStrikeIndicatorExpiration = (expiration: number) => {
  const expirationDate = dayjs(expiration);
  const currentYear = dayjs().year();
  const expirationYear = expirationDate.year();

  if (expirationYear > currentYear) {
    return expirationDate.format('YYYY-M-D');
  }

  return expirationDate.format('M-D');
};

export const getOptionLegPrice = (
  baseExpiration: number,
  baseStrike: number,
  optionType: OptionType,
  greeksData?: RawGreeksDataMap,
): number | null => {
  if (!greeksData) {
    return null;
  }

  const expirationData = greeksData[baseExpiration];
  if (!expirationData) {
    return null;
  }

  const strikeData = expirationData[baseStrike];
  if (!strikeData) {
    return null;
  }

  // Try to get call option price first
  const optionData =
    strikeData[
      optionType === OptionType.CALL ? OPTION_IDX.CALL : OPTION_IDX.PUT
    ];
  if (optionData && optionData.length > GREEK_IDX.ASK) {
    return getMidpointPrice(optionData);
  }

  return null;
};

export interface PositionGreeks {
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
}

export const getPositionGreeks = (
  optionLegs: PositionLeg[],
  shares: PositionShares[],
  greeksData?: RawGreeksDataMap,
): PositionGreeks => {
  const totals: PositionGreeks = {
    delta: getSharesDelta(shares),
    gamma: 0,
    vega: 0,
    theta: 0,
  };
  if (!greeksData) {
    return totals;
  }

  return optionLegs.reduce((acc, leg) => {
    const optionData =
      greeksData[leg.expiry]?.[leg.strike]?.[
        leg.option_type === OptionType.CALL ? OPTION_IDX.CALL : OPTION_IDX.PUT
      ];
    if (!optionData) {
      return acc;
    }
    acc.delta += leg.quantity * (optionData[GREEK_IDX.DELTA] ?? 0);
    acc.gamma += leg.quantity * (optionData[GREEK_IDX.GAMMA] ?? 0);
    acc.vega += leg.quantity * (optionData[GREEK_IDX.VEGA] ?? 0);
    acc.theta += leg.quantity * (optionData[GREEK_IDX.THETA] ?? 0);
    return acc;
  }, totals);
};

export const getOptionLegsCostDollars = (optionLegs: PositionLeg[]): number =>
  optionLegs.reduce(
    (sum, leg) => sum + leg.price_paid * CONTRACT_MULTIPLIER * leg.quantity,
    0,
  );

export const getSharesCostDollars = (shares: PositionShares[]): number =>
  shares.reduce((sum, share) => sum + share.price_paid * share.quantity, 0);

// Positive is a net debit, negative a net credit.
export const getNetCostDollars = (
  optionLegs: PositionLeg[],
  shares: PositionShares[],
): number =>
  getOptionLegsCostDollars(optionLegs) + getSharesCostDollars(shares);

export interface LegExpiryCorrection {
  legId: number;
  expiry: number;
}

// Matches a leg to the feed's real timestamp for its calendar day (legs built
// from a date-only string miss it); rolls forward to the nearest expiry if its day isn't in the feed at all.
export const buildLegExpiryCorrections = (
  legs: PositionLeg[],
  greeksData: RawGreeksDataMap,
): LegExpiryCorrection[] => {
  const availableExpiries = Object.keys(greeksData).map(Number);
  if (availableExpiries.length === 0) {
    return [];
  }

  const corrections: LegExpiryCorrection[] = [];
  const unmatchedLegs: PositionLeg[] = [];
  legs.forEach((leg) => {
    if (greeksData[leg.expiry] != null) {
      return;
    }
    const sameDayExpiry = availableExpiries.find((expiry) =>
      dayjs(expiry).tz(ET).isSame(dayjs(leg.expiry).tz(ET), 'day'),
    );
    if (sameDayExpiry != null) {
      corrections.push({ legId: leg.leg_id, expiry: sameDayExpiry });
    } else {
      unmatchedLegs.push(leg);
    }
  });

  if (unmatchedLegs.length > 0) {
    const earliestUnmatchedExpiry = Math.min(
      ...unmatchedLegs.map((leg) => leg.expiry),
    );
    const nearestAvailableExpiry = Math.min(...availableExpiries);
    const expiryOffset = nearestAvailableExpiry - earliestUnmatchedExpiry;
    unmatchedLegs.forEach((leg) => {
      corrections.push({
        legId: leg.leg_id,
        expiry: leg.expiry + expiryOffset,
      });
    });
  }

  return corrections;
};

export const generateDefaultOptionLeg = (
  sym: string,
  getRiskFreeRate: (tte: number) => number,
  greeksData?: RawGreeksDataMap,
  currentPrice?: number,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  optionType: OptionType = OptionType.CALL,
  exerciseStyles: ExerciseStylesRecord | null = null,
): PositionLeg | null => {
  if (!greeksData || !currentPrice) {
    return null;
  }

  // Use the appropriate strategy type based on the requested option type
  const strategyType =
    optionType === OptionType.PUT
      ? StrategyType.LONG_PUT
      : StrategyType.LONG_CALL;

  // Use the existing strategy generation function
  const legs = generateOptionLegsFromStrategy(
    strategyType,
    greeksData,
    sym,
    currentPrice,
    getRiskFreeRate,
    dividendDates,
    dividendAmounts,
    exerciseStyles,
  );

  // Return the first leg if available
  return legs.length > 0 ? legs[0] : null;
};

const getExpiration = (
  greeksData: RawGreeksDataMap,
  targetDte: number,
): number | null => {
  // Calculate target expiration timestamp (current time + target DTE)
  const targetExpiration = dayjs().add(targetDte, 'days').valueOf();

  // Get all available expirations and sort them
  const expirations = Object.keys(greeksData)
    .map(Number)
    .sort((a, b) => a - b);

  if (expirations.length === 0) {
    return null;
  }

  // Find expiration closest to target DTE
  const closestExpirationIdx = findNearestIdx(expirations, targetExpiration);
  const closestExpiration = expirations[closestExpirationIdx];
  return closestExpiration in greeksData ? closestExpiration : null;
};

const generateOptionLeg = (
  sym: string,
  greeksData: RawGreeksDataMap,
  targetDelta: number,
  targetDte: number,
  quantity: number,
  optionType: OptionType,
  currentPrice: number,
  getRiskFreeRate: (tte: number) => number,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  exerciseStyles: ExerciseStylesRecord | null = null,
): PositionLeg | null => {
  // Calculate target expiration timestamp (current time + target DTE)
  const expiry = getExpiration(greeksData, targetDte);
  if (expiry == null) {
    return null;
  }

  // Get strikes data for the closest expiration
  const expirationData = greeksData[expiry];
  const strikes = Object.keys(expirationData).map(Number);

  if (strikes.length === 0) {
    return null;
  }

  // Find the strike with delta closest to target delta
  const targetDeltaValue =
    optionType === OptionType.CALL ? targetDelta : -targetDelta;

  const strikeDataList: Array<{
    strike: number;
    callPrice: number;
    putPrice: number;
  }> = [];
  const currentTime = Date.now();
  const timeToExpirationYears = Math.max(
    (expiry - currentTime) / YEAR_IN_MS,
    0,
  );

  for (const strike of strikes) {
    const strikeData = expirationData[strike];
    const callGreeksData = strikeData?.[OPTION_IDX.CALL];
    const putGreeksData = strikeData?.[OPTION_IDX.PUT];

    if (
      !callGreeksData ||
      callGreeksData.length <= GREEK_IDX.ASK ||
      !putGreeksData ||
      putGreeksData.length <= GREEK_IDX.ASK
    ) {
      continue;
    }

    const callPrice = getMidpointPrice(callGreeksData);
    const putPrice = getMidpointPrice(putGreeksData);

    strikeDataList.push({ strike, callPrice, putPrice });
  }

  if (strikeDataList.length === 0) {
    return null;
  }

  const strikesArray = new Float64Array(strikeDataList.map((d) => d.strike));
  const callPricesArray = new Float64Array(
    strikeDataList.map((d) => d.callPrice),
  );
  const putPricesArray = new Float64Array(
    strikeDataList.map((d) => d.putPrice),
  );
  const impliedVols = calculateImpliedVolatilities(
    currentPrice,
    strikesArray,
    callPricesArray,
    putPricesArray,
    timeToExpirationYears,
    getRiskFreeRate(timeToExpirationYears),
    currentTime,
    Array.from(CLOSURES),
    dividendDates,
    dividendAmounts,
    false,
    resolveStyleForExpiry(exerciseStyles, expiry),
  );

  let bestStrike = strikeDataList[0]?.strike;
  let bestDeltaDiff: number | null = null;
  let bestOptionPrice: number | null = strikeDataList[0]
    ? optionType === OptionType.CALL
      ? strikeDataList[0].callPrice
      : strikeDataList[0].putPrice
    : null;

  for (const [idx, { strike }] of strikeDataList.entries()) {
    const impliedVol = impliedVols[idx];
    if (impliedVol === undefined) {
      continue;
    }
    const bsmGreeks = getBsmGreeksForOptionType(
      currentPrice,
      expiry,
      strike,
      optionType,
      impliedVol,
      getRiskFreeRate,
      exerciseStyles,
    );

    const calculatedDelta = bsmGreeks.delta;
    const deltaDiff = Math.abs(calculatedDelta - targetDeltaValue);

    if (bestDeltaDiff == null || deltaDiff < bestDeltaDiff) {
      bestDeltaDiff = deltaDiff;
      bestStrike = strike;
      bestOptionPrice = bsmGreeks.price;
    }
  }

  return {
    leg_id: shortIdGenerator(),
    option_type: optionType,
    strike: bestStrike,
    quantity,
    expiry,
    price_paid: Math.round((bestOptionPrice ?? 0) * 100) / 100,
    sym,
  };
};

export const generateOptionLegsFromStrategy = (
  strategy: StrategyType,
  greeksData: RawGreeksDataMap,
  sym: string,
  currentPrice: number,
  getRiskFreeRate: (tte: number) => number,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  exerciseStyles: ExerciseStylesRecord | null = null,
): PositionLeg[] => {
  // Helper function to create multiple legs with different parameters
  const createLegs = (legConfigs: Array<StrategyLeg>) => {
    const allLegs = legConfigs.map(({ delta, dte, quantity, optionType }) =>
      generateOptionLeg(
        sym,
        greeksData,
        delta,
        dte,
        quantity,
        optionType,
        currentPrice,
        getRiskFreeRate,
        dividendDates,
        dividendAmounts,
        exerciseStyles,
      ),
    );
    const legs = allLegs.filter(Boolean) as PositionLeg[];
    const mirrorCfgs = legConfigs
      .map((cfg, idx) => (cfg.mirror ? [cfg, cfg.mirror, idx] : null))
      .filter(Boolean) as [StrategyLeg, StrategyMirrorLeg, number][];

    if (mirrorCfgs.length === 0) {
      return legs;
    }

    // Calculate target expiration timestamp (current time + target DTE)
    const mirrorLegs = mirrorCfgs
      .map(([cfg, mirror, idx]) => {
        const leg = allLegs[idx];
        if (leg == null) {
          return null;
        }
        const quantity = mirror.quantity ?? cfg.quantity;
        const expiry = getExpiration(greeksData, cfg.dte);
        const strikeMap = greeksData[expiry!];
        const tgtStrike =
          mirror.target === 'fwdPrice'
            ? 2 * findD50Price(strikeMap) - leg.strike
            : 2 * legs[mirror.target.index].strike - leg.strike;

        const strikes = Object.keys(strikeMap)
          .map(Number)
          .sort((a, b) => a - b);
        const sidx: number = findNearestIdx(strikes, tgtStrike);
        const optionIdx =
          mirror.optionType === OptionType.CALL
            ? OPTION_IDX.CALL
            : OPTION_IDX.PUT;
        const optionPrice =
          getMidpointPrice(strikeMap[strikes[sidx]][optionIdx]) || 0;
        return {
          leg_id: shortIdGenerator(),
          option_type: mirror.optionType,
          strike: strikes[sidx],
          quantity,
          expiry,
          price_paid: Math.round(optionPrice * 100) / 100,
          sym,
        };
      })
      .filter(Boolean) as PositionLeg[];
    return legs.concat(mirrorLegs).sort((a, b) => a.strike - b.strike);
  };

  // Strategy configurations
  const config = STRATEGY_CONFIGS[strategy];
  return config ? createLegs(config) : [];
};

export const getUpdatedLeg = (
  leg: PositionLeg,
  updatedFields: Partial<PositionLeg>,
  shouldUseDefaultPrice: boolean,
  greeks: RawGreeksDataMap,
): PositionLeg => {
  if (shouldUseDefaultPrice) {
    const defaultPrice =
      leg &&
      getOptionLegPrice(
        updatedFields.expiry ?? leg.expiry,
        updatedFields.strike ?? leg.strike,
        updatedFields.option_type ?? leg.option_type,
        greeks,
      );
    if (defaultPrice != null) {
      updatedFields.price_paid = Math.round(defaultPrice * 1_000) / 1_000; // Round to 3 decimal places
    }
  }
  return { ...leg, ...updatedFields };
};

export const hasModifiedIVSettings = (
  ivSettings: IVAdjustmentSettings[],
  currentExpirations: number[],
): boolean => {
  if (ivSettings.length === 0) {
    return false;
  }
  return currentExpirations.some((expiration) => {
    const iv = ivSettings.find((iv) => iv.expiry === expiration);
    if (iv == null) {
      return false;
    }

    return !matchesDefaultIVSettings(iv);
  });
};

export const matchesDefaultIVSettings = (
  ivSettings: Partial<IVAdjustmentSettings>,
): boolean => {
  return (
    ivSettings.level_shift === OC_DEFAULT_LEVEL_SHIFT &&
    ivSettings.call_slope_mult === OC_DEFAULT_CALL_SLOPE_MULT &&
    ivSettings.put_slope_mult === OC_DEFAULT_PUT_SLOPE_MULT
  );
};

export const getPositionDeletes = (
  position: Position,
  originalPosition: Position,
): PositionDeletes => {
  const deletes: PositionDeletes = {};

  // Find adj_ids that were in original but not in current position
  const currentAdjIds = new Set(
    position.iv_adjustments
      .map((adj) => Number(adj.adj_id))
      .filter(Number.isInteger),
  );
  const originalAdjIds = originalPosition.iv_adjustments
    .map((adj) => Number(adj.adj_id))
    .filter(Number.isInteger);
  const deletedAdjIds = originalAdjIds.filter((id) => !currentAdjIds.has(id));

  if (deletedAdjIds.length > 0) {
    deletes.adj_ids = deletedAdjIds;
  }

  // Find leg_ids that were in original but not in current position
  const currentLegIds = new Set(
    position.option_legs
      .map((leg) => Number(leg.leg_id))
      .filter(Number.isInteger),
  );
  const originalLegIds = originalPosition.option_legs
    .map((leg) => Number(leg.leg_id))
    .filter(Number.isInteger);
  const deletedLegIds = originalLegIds.filter((id) => !currentLegIds.has(id));

  if (deletedLegIds.length > 0) {
    deletes.leg_ids = deletedLegIds;
  }

  // Find underlying_share_syms that were in original but not in current position
  const currentShareSyms = new Set(
    position.underlying_shares.map((share) => share.sym),
  );
  const originalShareSyms = originalPosition.underlying_shares.map(
    (share) => share.sym,
  );
  const deletedShareSyms = originalShareSyms.filter(
    (sym) => !currentShareSyms.has(sym),
  );

  if (deletedShareSyms.length > 0) {
    deletes.underlying_share_syms = deletedShareSyms;
  }

  return deletes;
};

export const getBsmGreeksForOptionType = (
  underlyingPrice: number,
  expiration: number,
  strike: number,
  optionType: OptionType,
  iv: number,
  getRiskFreeRate: (tte: number) => number,
  exerciseStyles: ExerciseStylesRecord | null = null,
): GreeksValues => {
  const now = dayjs().valueOf();
  const yte = getTteYearsFromBaseDate(now, expiration);

  const result = calculateOptionPriceAndGreeks(
    underlyingPrice,
    strike,
    yte,
    { getRiskFreeRate } as ModelParameters,
    iv,
    optionType === OptionType.CALL,
    resolveStyleForExpiry(exerciseStyles, expiration),
  );

  result.vega = result.vega / 100;
  result.theta = getThetaPerDay(
    result,
    underlyingPrice,
    strike,
    optionType === OptionType.CALL,
    (expiration - now) / MILLISECONDS_PER_DAY,
  );

  return result;
};

export const formatPnlTimeToExpiration = (timeInMs: number): string => {
  // Check if we're at expiration (0 time remaining)
  if (timeInMs <= 0) {
    return 'At Expiration';
  }

  if (timeInMs < MILLISECONDS_PER_DAY) {
    const hours = timeInMs / MILLISECONDS_PER_HOUR;

    if (hours < 0.1) {
      return '<0.1h';
    }

    return `${hours.toFixed(1)}h`;
  } else {
    const days = Math.round(timeInMs / MILLISECONDS_PER_DAY);

    if (days === 0) {
      return '<1d';
    }

    return `${days}d`;
  }
};

export const formatPnlTteDateDisplay = (
  timestamp: number,
  latestLegExpiration: number,
  tickConfig: Map<number, number>,
  isMobile: boolean = false,
): string => {
  const selectedDate = dayjs(timestamp);
  const timeRemaining = Math.max(0, latestLegExpiration - timestamp);

  if (timeRemaining <= 0) {
    return `${selectedDate.format('YYYY-MM-DD')} (At Expiration)`;
  }

  const timeRemainingDays = timeRemaining / MILLISECONDS_PER_DAY;
  const stepHours = getStepHours(timeRemainingDays, tickConfig);
  const displayFormat = formatTimeRemaining(timeRemaining, stepHours);

  const dateFormat = isMobile ? 'MM/DD' : 'YYYY-MM-DD';
  const suffix = isMobile ? displayFormat : `${displayFormat} remaining`;

  return `${selectedDate.format(dateFormat)} (${suffix})`;
};

function getStepHours(
  timeRemainingDays: number,
  tickConfig: Map<number, number>,
): number {
  const dtes = [...tickConfig.keys()].sort((a, b) => b - a);

  for (const dte of dtes) {
    if (timeRemainingDays >= dte) {
      return tickConfig.get(dte)!;
    }
  }

  return 0.5; // default to 30 minutes
}

function formatTimeRemaining(timeRemaining: number, stepHours: number): string {
  if (stepHours >= 24) {
    return formatDays(timeRemaining);
  }

  if (stepHours >= 1) {
    return formatHours(timeRemaining, stepHours);
  }

  return formatMinutes(timeRemaining, stepHours);
}

function formatDays(timeRemaining: number): string {
  const days = Math.round(timeRemaining / MILLISECONDS_PER_DAY);
  return `${days}d`;
}

function formatHours(timeRemaining: number, stepHours: number): string {
  const totalHours = timeRemaining / MILLISECONDS_PER_HOUR;
  const roundedHours = Math.round(totalHours / stepHours) * stepHours;

  if (roundedHours >= 24) {
    const days = Math.floor(roundedHours / 24);
    const remainingHours = roundedHours % 24;
    return remainingHours === 0 ? `${days}d` : `${days}d ${remainingHours}h`;
  }

  return `${roundedHours}h`;
}

function formatMinutes(timeRemaining: number, stepHours: number): string {
  const totalMinutes = timeRemaining / (1_000 * 60);
  const stepMinutes = stepHours * 60;
  const roundedMinutes = Math.round(totalMinutes / stepMinutes) * stepMinutes;

  if (roundedMinutes >= 60) {
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;
    return remainingMinutes === 0
      ? `${hours}h`
      : `${hours}h ${remainingMinutes}m`;
  }

  return `${roundedMinutes}m`;
}
